'use client';
import React, { useEffect, useState } from 'react';
import {
  CalendarDaysIcon,
  AdjustmentsVerticalIcon,
  UserPlusIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { numberWithCommas, roundNumberToXDecimals } from '@/lib/numberFormatting';
import Stats from '@/sections/pricing/RoiCalculatorSection/Stats';
import Button from '@/components/Button';
import IconTitleNumberWrapInput from './IconTitleNumberWrapInput';

const RoiCalculator = () => {
  const [numberOfDevelopers, setNumberOfDevelopers] = useState(200);
  const [annualSalaryPerDeveloper, setAnnualSalaryPerDeveloper] = useState(125_000);
  const [costPerDeveloperPerMinute, setCostPerDeveloperPerMinute] = useState(0);
  const [localBuildsPerDayPerDeveloper, setLocalBuildsPerDayPerDeveloper] = useState(0);
  const [ciBuildsPerWeek, setCiBuildsPerWeek] = useState(0);

  const [savedDevHours, setSavedDevHours] = useState(0);
  const [annualHoursSaved, setAnnualHoursSaved] = useState(0);
  const [annualSavings, setAnnualSavings] = useState(0);

  const handleChangeNumberOfDevelopers = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNumberOfDevelopers(+e.target.value);
  };
  const handleChangeAnnualSalaryPerDeveloper = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnnualSalaryPerDeveloper(+e.target.value);
  };

  useEffect(() => {
    const calculateBuildsAndTestsPerWeek = () => {
      const total = numberOfDevelopers * 5 * 5 + numberOfDevelopers * 5 * 10;
      setLocalBuildsPerDayPerDeveloper(total);
      setCiBuildsPerWeek(total * 5);

      // Saved Developer hours per week (per developer)
      const savedTotal = ((0.5 * total * 5) / 60 + (40 * 0.0375 * total) / 60) / numberOfDevelopers;
      setSavedDevHours(savedTotal);

      const savedTotalPerYear = savedTotal * numberOfDevelopers * 48;
      setAnnualHoursSaved(savedTotalPerYear);
    };

    calculateBuildsAndTestsPerWeek();
  }, [numberOfDevelopers]);

  useEffect(() => {
    const annualSavingsTotal = (annualSalaryPerDeveloper / 48 / 5 / 8) * annualHoursSaved;
    setAnnualSavings(roundNumberToXDecimals(annualSavingsTotal, 0));
  }, [annualSalaryPerDeveloper, annualHoursSaved]);

  useEffect(() => {
    const newCostPerDeveloperPerMinute = roundNumberToXDecimals(annualSalaryPerDeveloper / 48 / 5 / 8 / 60, 2);
    setCostPerDeveloperPerMinute(newCostPerDeveloperPerMinute);
  }, [annualSalaryPerDeveloper]);

  return (
    <div className="col-span-2 mx-auto flex flex-col items-center justify-center gap-x-10 gap-y-8 md:flex-row lg:flex-row xl:flex-row xl:gap-x-14">
      <div className="flex flex-1 flex-col gap-12 rounded-4xl p-8 shadow-card-light">
        <IconTitleNumberWrapInput
          text="Number of developers"
          displayNumber={numberOfDevelopers}
          icon={<UserPlusIcon className="size-6 min-w-[1.5rem] text-gradle-green" />}
        >
          <input
            onChange={handleChangeNumberOfDevelopers}
            type="range"
            min={20}
            max={2000}
            value={numberOfDevelopers}
            className="range-primary range"
          />
        </IconTitleNumberWrapInput>
        <IconTitleNumberWrapInput
          text="Annual salary per developer"
          displayNumber={`$${numberWithCommas(annualSalaryPerDeveloper)}`}
          subDisplayNumber={costPerDeveloperPerMinute}
          subDisplayText="/per mn"
          icon={<CurrencyDollarIcon className="size-6 min-w-[1.5rem] text-gradle-green" />}
        >
          <input
            onChange={handleChangeAnnualSalaryPerDeveloper}
            type="range"
            min={50_000}
            max={500_000}
            value={annualSalaryPerDeveloper}
            className="range-primary range"
          />
        </IconTitleNumberWrapInput>
        <IconTitleNumberWrapInput
          text="Local & CI Builds & Tests per&nbsp;Week*"
          displayNumber={numberWithCommas(localBuildsPerDayPerDeveloper)}
          icon={<CalendarDaysIcon className="size-6 min-w-[1.5rem] text-gradle-green" />}
        />
        <IconTitleNumberWrapInput
          text="Total Build & Test Time per&nbsp;Week*"
          displayNumber={numberWithCommas(ciBuildsPerWeek)}
          subDisplayText="minutes"
          icon={<AdjustmentsVerticalIcon className="size-6 min-w-[1.5rem] text-gradle-green" />}
        />
        <p className="text-footnote-sm text-gray-4">*Based on industry build and test time and failure averages</p>
      </div>
      <div className="flex flex-1 flex-col gap-10 px-5 md:gap-14 lg:gap-14 xl:gap-14">
        <Stats
          amount={savedDevHours}
          decimals={1}
          units="hrs/week/developer"
          text="Every developer saves this per&nbsp;week"
        />
        <Stats amount={annualHoursSaved} units="hrs/yr" text="Annual time savings" />
        <Stats amount={annualSavings} currency={true} text="Annual cost savings" />
        <Button href="/roi-calculator/" variant="primary">
          See the expanded calculator
        </Button>
      </div>
    </div>
  );
};

export default RoiCalculator;
